/* Экран «Повтори движения» / «Стать персонажем» (APP-SPEC-5 §20, разметка bot-dance.js): шапка с названием пресета, «1. Видео с движениями»
   (рамка с примером «до → после», бейдж цены за единицу, фрагмент), строка состояния, «2. Кто повторит» (ряд лиц, разовое фото с кадром,
   подпись-правило и согласие), карточка «Ещё настройки» (Голос, Фон), кнопка «Создать · N C»; хвост заказа — MotionTail.
   Уходят только ключи формы сервера (fields / screen_fields); чего форма не публикует, экран не показывает. */
import { useEffect, useRef, useState } from 'react';
import { api, endpoints } from '../../api';
import type { UserAvatar } from '../../api/people';
import type { Voice } from '../../api/types';
import { uiFormat, uiText } from '../../i18n';
import { studioApi, studioCheapOffer, type StudioApp } from '../studio/studio-api';
import { useStudioDocument } from '../studio/use-studio-document';
import { useStudioForm } from '../studio/use-studio-form';
import { record, rows, text } from '../studio/studio-content';
import { ToolHeader } from '../studio/circle/ToolHeader';
import { ToolCreateButton } from '../studio/circle/ToolCreateButton';
import { ToolIcon } from '../studio/circle/ToolIcon';
import { cNum } from '../studio/circle/order-tail-model';
import { StudioSheet } from '../studio/StudioSheet';
import { artworkUrl } from '../studio/studio-art-model';
import { useVideoSourcePreview } from '../video/use-video-source-preview';
import { MotionHero } from './MotionHero';
import { MotionClip, type MotionClipMeta } from './MotionClip';
import { MotionConsent } from './MotionConsent';
import { MotionCrop } from './MotionCrop';
import { CROP_CY, type MotionCropValue } from './motion-crop-model';
import { MotionSettings } from './MotionSettings';
import { MotionTail } from './MotionTail';
import { useMotionCast, type MotionChoice } from './use-motion-cast';
import {
  motionFragment,
  motionHeroKind,
  motionHumanFound,
  motionLimits,
  motionMissing,
  motionPolicy,
  motionPrice,
  motionRatioWord,
  motionStatus,
  motionUnit,
  motionVoiceKey,
  motionWords,
  resetHeroCrop,
  type MotionHeroInfo,
  type MotionSnapshot,
} from './motion-model';
import { useMotionDraft } from './use-motion-draft';
import '../photo/server-photo.css';
import '../studio/native-template.css';
import './motion.css';

interface Props {
  app: StudioApp;
  avatar: UserAvatar | null;
  initialInputs?: Record<string, unknown>;
  /** Персонажи входа, которые встают первыми в ряду (o.chars) */
  firstChars?: number[];
}
export function MotionOrderForm(props: Props) {
  const [app, setApp] = useState(props.app),
    [seed, setSeed] = useState(props.initialInputs),
    [switching, setSwitching] = useState(false),
    [error, setError] = useState('');
  const request = useRef<AbortController | null>(null),
    drafts = useRef(new Map<string, Record<string, string>>());
  useEffect(() => () => request.current?.abort(), []);
  const screen = useStudioDocument(signal => api.request<Record<string, unknown>>('/app/studio/screens/dance', { signal }), 'motion-tools');
  const tools = rows(record(screen.data?.dance).tools).filter(
    t => Number.isSafeInteger(t.app_id) && Number(t.app_id) > 0 && ['dance', 'swap'].includes(text(t.preset)),
  );
  return (
    <section className="server-video server-motion native-studio-form">
      {error && <p role="alert">{uiText(error)}</p>}
      {screen.error && (
        <p role="alert">
          {uiText('Не удалось загрузить режимы видео.')}{' '}
          <button type="button" onClick={screen.reload}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
      <Body
        key={app.id}
        app={app}
        avatar={props.avatar}
        initialInputs={seed}
        firstChars={props.firstChars}
        tools={tools}
        resultActions={record(screen.data?.dance).result_actions}
        switching={switching}
        onSwitch={async (id, inputs, owner) => {
          if (switching || id === app.id || !owner) return;
          setSwitching(true);
          setError('');
          request.current?.abort();
          const controller = new AbortController();
          request.current = controller;
          try {
            const me = await endpoints.me();
            if (me.user.id !== owner) {
              drafts.current.clear();
              throw Error('owner_changed');
            }
            drafts.current.set(`${owner}:${app.id}`, { ...inputs });
            const next = await studioApi.app(id, controller.signal);
            if (controller.signal.aborted) return;
            if (!['dance', 'swap'].includes(next.entry.screen) || (await endpoints.me()).user.id !== owner)
              throw Error('wrong_owner_or_screen');
            setSeed(drafts.current.get(`${owner}:${id}`));
            setApp(next);
          } catch {
            if (!controller.signal.aborted) setError('Не удалось открыть режим. Попробуйте ещё раз.');
          } finally {
            if (!controller.signal.aborted) setSwitching(false);
          }
        }}
      />
    </section>
  );
}
/** Сведения о герое для строки состояния и кнопки: имя и «нет фото». */
function heroInfo(inputs: Record<string, string>, choice: MotionChoice | undefined): MotionHeroInfo | null {
  if (inputs.photo) return { name: uiText('Человек с фото') };
  if (!choice) return null;
  return { name: choice.name, needsPhoto: choice.needsPhoto };
}
function Body({
  app,
  avatar,
  initialInputs,
  firstChars,
  tools,
  resultActions,
  switching,
  onSwitch,
}: Props & {
  tools: Record<string, unknown>[];
  resultActions: unknown;
  switching: boolean;
  onSwitch(id: number, inputs: Record<string, string>, owner?: number): void;
}) {
  const base = useStudioForm({ app, avatar, initialInputs });
  const draft = useMotionDraft(base, !!initialInputs);
  const draftBlocked = draft.blocked || draft.restoring;
  const state = { ...base, locked: base.locked || draftBlocked, submissionBlocked: base.submissionBlocked || draftBlocked },
    { form, inputs, rights, locked, busy, submissionBlocked, intent, orderId, error } = state;
  const [clip, setClip] = useState<MotionClipMeta | null>(null);
  const [voice, setVoice] = useState<Voice | null>(null);
  const [crop, setCrop] = useState<{ url: string; file: File | null } | null>(null);
  const [waitingPhoto, setWaitingPhoto] = useState<File | null>(null);
  const [photoError, setPhotoError] = useState('');
  const [localPhoto, setLocalPhoto] = useState<string | null>(null);
  useEffect(
    () => () => {
      if (localPhoto) URL.revokeObjectURL(localPhoto);
    },
    [localPhoto],
  );
  const policy = form ? motionPolicy(form) : null;
  const words = motionWords(policy?.preset);
  const cast = useMotionCast(state, avatar, firstChars);
  const photoField = form?.fields.find(f => f.key === 'photo' && f.upload?.kind === 'photo');
  const photoPreview = useVideoSourcePreview(inputs.photo, state.ownerId, 'photo', photoField?.upload?.max_bytes ?? 10 * 1024 * 1024);
  const photoUrl = localPhoto && inputs.photo ? localPhoto : photoPreview.url;
  const choice = cast.choices?.find(c => c.value === inputs.cast);
  const hero = heroInfo(inputs, choice);
  const heroKind = motionHeroKind(inputs);
  /* картинка персонажа — ключ каталога (live-avatar/<id>) или уже полученный blob; в <img> идёт только адрес, как у строки героя */
  const heroPicture = inputs.photo ? photoUrl || null : artworkUrl(choice?.image ?? null);
  const limits = form ? motionLimits(form, inputs) : { minMs: 2000, maxMs: 15000 };
  const missing = form ? motionMissing(form, inputs, rights, { hero, cropOpen: !!crop }) : null;
  const status = form ? motionStatus(form, inputs, clip, hero, motionHumanFound(form, null)) : null;
  const video = form?.fields.find(f => f.key === 'video' && f.upload?.kind === 'video');
  const price = form ? motionPrice(app, form, inputs) : { price: null, from: false, estimate: false };
  const unit = motionUnit(app, form);
  const ratio = clip && clip.width > 0 && clip.height > 0 ? clip.width / clip.height : 9 / 16;
  const cropFields = ['crop_x', 'crop_y', 'zoom'].every(key => form?.fields.some(f => f.key === key));
  const voiceKey = form ? motionVoiceKey(form) : 'voice';

  const openPhoto = (file: File) => {
    setPhotoError('');
    if (!photoField?.upload?.accept.includes(file.type)) {
      setPhotoError('Нужно фото: JPG или PNG');
      return;
    }
    const max = photoField.upload.max_bytes ?? 10 * 1024 * 1024;
    if (file.size > max) {
      setPhotoError(uiFormat('Файл слишком большой. Максимум {0} МБ', Math.round(max / 1024 / 1024)));
      return;
    }
    if (!rights || inputs.consent_accepted !== 'true') {
      setWaitingPhoto(file);
      return;
    }
    setCrop({ url: URL.createObjectURL(file), file });
  };
  const closeCrop = () => {
    if (crop?.file) URL.revokeObjectURL(crop.url);
    setCrop(null);
  };
  const finishCrop = async (value: MotionCropValue) => {
    if (!crop) return;
    const file = crop.file;
    const values: Record<string, string> = cropFields ? { crop_x: value.cx.toFixed(4), crop_y: value.cy.toFixed(4), zoom: value.z.toFixed(2) } : {};
    if (file) {
      if (inputs.photo || state.uploadIds.photo) state.clearUpload('photo');
      state.setInputs(v => {
        const next: Record<string, string> = { ...resetHeroCrop(v), ...values };
        delete next.cast;
        return next;
      });
      setLocalPhoto(crop.url);
      setCrop(null);
      await state.upload('photo', file);
    } else {
      state.setInputs(v => ({ ...v, ...values }));
      setCrop(null);
    }
  };
  const snapshot: MotionSnapshot = {
    preset: policy?.preset ?? 'dance',
    kind: heroKind,
    name: heroKind === 'avatar' && choice ? `${uiText('Вы')} · ${choice.name}` : heroKind === 'me' ? uiText('Вы') : (hero?.name ?? ''),
    female: !!choice?.female,
    fileName: clip?.name ?? '',
    width: clip?.width ?? 0,
    height: clip?.height ?? 0,
    lenMs: motionFragment(inputs)?.lenMs ?? 0,
    bg: inputs.bg === 'hero' ? 'hero' : 'video',
    voiceName: inputs[voiceKey] === 'voice' && voice ? voice.name : '',
  };
  const header = (
    <ToolHeader
      title={words.title}
      aside={
        !orderId && tools.length > 1 ? (
          <nav className="seg anim ap-src pf-src cseg" aria-label={uiText('Режим движения')}>
            {tools.map(t => (
              <button
                key={Number(t.app_id)}
                type="button"
                aria-pressed={Number(t.app_id) === app.id}
                className={Number(t.app_id) === app.id ? 'on' : undefined}
                disabled={locked || switching || !state.ownerId}
                onClick={() => onSwitch(Number(t.app_id), inputs, state.ownerId)}
              >
                {text(t.title) || uiText(t.preset === 'swap' ? 'Стать персонажем' : 'Повтори движения')}
              </button>
            ))}
          </nav>
        ) : undefined
      }
    />
  );
  const example = (
    <span className="dn-ba" aria-hidden="true">
      <span className="f motion-ph-tile">
        <ToolIcon kind="video" />
        <small>{uiText('До')}</small>
      </span>
      <span className="ar">
        <ToolIcon kind="arrow" />
      </span>
      <span className="f af">
        <span className="dn-af">{heroPicture ? <img src={heroPicture} alt="" draggable={false} /> : <span className="motion-ph-tile"><ToolIcon kind="user" /></span>}</span>
        <small>{uiText('После')}</small>
      </span>
    </span>
  );
  const badge =
    unit !== null && form?.chargeable ? (
      <span className="bc-pb pb-corner bcu-pb">
        <span className="mono">{cNum(unit)}</span> {uiText('C за секунду')}
      </span>
    ) : undefined;
  return (
    <>
      {header}
      {draft.restoring && <p role="status">{uiText('Восстанавливаем черновик…')}</p>}
      {draft.blocked && (
        <p role="alert">
          {uiText('Не удалось восстановить или сохранить черновик.')}{' '}
          <button type="button" onClick={() => void draft.reset()}>
            {uiText('Сбросить сохранённый черновик')}
          </button>
        </p>
      )}
      {draft.unavailableFile && <p role="alert">{uiText('Некоторые файлы черновика больше недоступны. Загрузите их заново.')}</p>}
      {!form && !error && <p role="status">{uiText('Загружаем…')}</p>}
      {error && (
        <p role="alert">
          {uiText(error)}{' '}
          {!intent && (
            <button type="button" onClick={() => state.setRevision(v => v + 1)}>
              {uiText('Обновить')}
            </button>
          )}
        </p>
      )}
      {form && !orderId && !draft.restoring && (
        <form
          className="pf-body dn-body"
          onSubmit={e => {
            e.preventDefault();
            if (!busy && !submissionBlocked && (intent || !missing)) void state.submit();
          }}
        >
          {crop ? (
            <MotionCrop
              url={crop.url}
              ratio={ratio}
              ratioWord={clip ? motionRatioWord(clip.width, clip.height) : 'Сторис'}
              initial={
                !crop.file && inputs.crop_x
                  ? { cx: Number(inputs.crop_x) || 0.5, cy: Number(inputs.crop_y) || CROP_CY, z: Number(inputs.zoom) || 1 }
                  : null
              }
              disabled={locked}
              onDone={value => void finishCrop(value)}
              onCancel={closeCrop}
              onOther={file => {
                closeCrop();
                openPhoto(file);
              }}
            />
          ) : (
            <>
              {video && (
                <MotionClip
                  field={video}
                  state={state}
                  words={words}
                  limits={limits}
                  badge={badge}
                  example={example}
                  meta={clip}
                  onMeta={setClip}
                />
              )}
              <div className="dn-iss" aria-live="polite">
                {status && (
                  <div className={`note ${status.kind}`}>
                    <ToolIcon kind={status.kind === 'ok' ? 'check' : status.kind === 'bad' ? 'warn' : 'info'} />
                    <span>{uiText(status.text)}</span>
                  </div>
                )}
              </div>
              <MotionHero
                state={state}
                words={words}
                choices={cast.choices}
                error={cast.error}
                onRetry={cast.retry}
                photoUrl={inputs.photo ? photoUrl : ''}
                onPhotoFile={openPhoto}
                onPhotoAgain={() => {
                  if (photoUrl) setCrop({ url: photoUrl, file: null });
                }}
              />
              {photoError && <p role="alert">{uiText(photoError)}</p>}
              <MotionSettings
                state={state}
                hero={heroKind}
                heroVoice={choice?.voiceId ?? null}
                mute={clip?.hasAudio === false}
                voice={voice}
                onVoice={setVoice}
              />
            </>
          )}
          <ToolCreateButton
            label="Создать"
            price={price.price}
            from={price.from}
            chargeable={form.chargeable}
            missing={missing}
            busy={busy || switching}
            blocked={submissionBlocked}
            pending={!!intent}
            available={form.available}
            cheap={(() => {
              const offer = studioCheapOffer(form);
              return offer ? { price: offer.price, where: 'на видео', go: () => void state.submit({ cheap: true }) } : null;
            })()}
            onCreate={() => void state.submit()}
          />
        </form>
      )}
      {!form && intent && !orderId && (
        <button type="button" disabled={busy || submissionBlocked} onClick={() => void state.submit()}>
          {uiText('Продолжить проверку')}
        </button>
      )}
      {orderId && <MotionTail state={state} snapshot={snapshot} picture={heroPicture} resultActions={resultActions} />}
      {waitingPhoto && (
        <StudioSheet title="Права и согласие" onClose={() => setWaitingPhoto(null)}>
          <h2>{uiText('Права и согласие')}</h2>
          <MotionConsent state={state} />
          <button
            type="button"
            className="btn"
            disabled={locked || !rights || inputs.consent_accepted !== 'true'}
            onClick={() => {
              const file = waitingPhoto;
              setWaitingPhoto(null);
              setCrop({ url: URL.createObjectURL(file), file });
            }}
          >
            {uiText('Продолжить')}
          </button>
        </StudioSheet>
      )}
    </>
  );
}
